// src/components/TermsIconAdvanced.js
import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import "./TermsIconAdvanced.css";

const SECTION_LIST = [
  { id: "introduction", label: "Introduction" },
  { id: "eligibility", label: "Eligibility & Use" },
  { id: "bookings", label: "Bookings & Services" },
  { id: "payments-pricing", label: "Payments & Pricing" },
  { id: "refunds", label: "Refunds & Cancellations" },
  { id: "privacy", label: "Privacy & Data Protection" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "conduct", label: "User Conduct" },
  { id: "ip-rights", label: "Intellectual Property" },
  { id: "liability", label: "Liability & Warranties" },
  { id: "payments-security", label: "Payments & Security" },
  { id: "disputes", label: "Dispute Resolution & Governing Law" },
  { id: "changes", label: "Changes to Terms" },
  { id: "contact", label: "Contact Us" },
];

const TermsIconAdvanced = forwardRef(function TermsIconAdvanced(
  {
    /** Trigger:
     *  - "fab": floating 📜 button (default)
     *  - "button": inline button
     *  - "none": control via ref
     */
    trigger = "fab",
    label = "Terms & Conditions",
    buttonClassName = "btn-outline",
    /** side only affects `trigger="fab"` */
    side = "left", // default left per your request
    /** optional: override the “Last updated” date text */
    lastUpdated = "31 Aug 2025",
    jurisdiction = "India",
  },
  ref
) {
  const [open, setOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const modalRef = useRef(null);
  const firstFocusable = useRef(null);
  const lastFocusable = useRef(null);
  const previouslyFocused = useRef(null);

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
    close: () => setOpen(false),
    /** Open the modal and optionally scroll to a section id */
    openTo: (sectionId) => {
      setOpen(true);
      // wait for modal to mount, then scroll
      setTimeout(() => {
        if (sectionId) {
          const el = document.getElementById(sectionId);
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 0);
   },
  }));

  // Lock body scroll + focus manage
  useEffect(() => {
    if (open) {
      previouslyFocused.current = document.activeElement;
      document.body.style.overflow = "hidden";
      const t = setTimeout(() => firstFocusable.current?.focus(), 0);
      return () => clearTimeout(t);
    } else {
      document.body.style.overflow = "";
      previouslyFocused.current?.focus?.();
    }
  }, [open]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleOverlayClick = () => setOpen(false);
  const handleModalClick = (e) => e.stopPropagation();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const printTerms = () => window.print();

  return (
    <>
      {/* Floating trigger */}
      {trigger === "fab" && (
        <button
          className={`terms-fab ${side === "right" ? "right" : "left"}`}
          title="Terms & Policies"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open ? "true" : "false"}
        >
          <span aria-hidden="true">📜</span>
          <span className="sr-only">Open Terms & Conditions</span>
        </button>
      )}

      {/* Inline trigger */}
      {trigger === "button" && (
        <button
          className={buttonClassName}
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open ? "true" : "false"}
        >
          {label}
        </button>
      )}

      {/* Modal */}
      {open && (
        <div
          className="terms-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-title"
          onClick={handleOverlayClick}
        >
          <div className="terms-modal" onClick={handleModalClick} ref={modalRef}>
            {/* Header */}
            <div className="terms-header">
              <h2 id="terms-title">Terms & Conditions</h2>
              <div className="terms-header-actions">
                <button
                  className="btn secondary"
                  onClick={printTerms}
                  ref={firstFocusable}
                >
                  Print / Save PDF
                </button>
                <button
                  className="btn ghost"
                  onClick={() => setOpen(false)}
                  aria-label="Close terms"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Subhead */}
            <div className="terms-subhead">
              <p>
                <strong>Last updated:</strong> {lastUpdated} •{" "}
                <strong>Jurisdiction:</strong> {jurisdiction}
              </p>
              <p className="muted">
                These Terms govern your use of our website and services
                (wedding & event planning, design, destinations, entertainment,
                and related offerings) provided by{" "}
                <strong>Parivar Wedding Event</strong>. By using the site,
                you agree to these Terms.
              </p>

              {/* Mobile jump menu */}
              <div className="terms-mobile-nav">
                <label htmlFor="terms-nav" className="sr-only">
                  Jump to section
                </label>
                <select
                  id="terms-nav"
                  onChange={(e) => scrollTo(e.target.value)}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Jump to section...
                  </option>
                  {SECTION_LIST.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Body: Sidebar + Content */}
            <div className="terms-body">
              <aside className="terms-sidebar" aria-label="Sections">
                <nav>
                  <ul>
                    {SECTION_LIST.map((s) => (
                      <li key={s.id}>
                        <button
                          className="link"
                          onClick={() => scrollTo(s.id)}
                          aria-controls={s.id}
                        >
                          {s.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>

              <section className="terms-content" id="terms-scroll-region">
                {/* 1. Introduction */}
                <article id="introduction">
                  <h3>1. Introduction</h3>
                  <p>
                    Welcome to <strong>Parivar Wedding Event</strong>, a wedding and
                    event planning platform. By accessing or using our website
                    and services (including planning, design & décor, destination
                    management, entertainment, and related services), you agree
                    to be bound by these Terms & Conditions (“Terms”). If you do
                    not agree with these Terms, please discontinue use of the
                    website immediately.
                  </p>
                </article>

                {/* 2. Eligibility & Use */}
                <article id="eligibility">
                  <h3>2. Eligibility & Use</h3>
                  <ul>
                    <li>
                      You must be <strong>18 years or older</strong> (or have legal
                      guardian consent) to use our services.
                    </li>
                    <li>
                      Use of the website is permitted for{" "}
                      <strong>personal and lawful purposes only</strong>.
                    </li>
                    <li>
                      Any attempt to misuse the platform, engage in fraud, or
                      violate applicable laws may result in suspension or
                      termination of services.
                    </li>
                  </ul>
                </article>

                {/* 3. Bookings & Services */}
                <article id="bookings">
                  <h3>3. Bookings & Services</h3>
                  <ul>
                    <li>
                      All bookings are <strong>subject to vendor/venue availability</strong> and require written confirmation.
                    </li>
                    <li>
                      Service details, inclusions, and timelines will be
                      specified in proposals, letters of acceptance, or
                      agreements.
                    </li>
                    <li>
                      We may partner with <strong>third-party vendors</strong> (venues,
                      artists, caterers, photographers, logistics, etc.). Each
                      vendor may have additional terms you must accept.
                    </li>
                    <li>
                      We are not liable for vendor defaults, but we will use
                      best efforts to provide comparable alternatives.
                    </li>
                    <li>
                      <strong>Force Majeure</strong> (e.g., natural disasters, pandemics,
                      government orders, strikes, weather) may delay or prevent
                      fulfillment.
                    </li>
                  </ul>
                </article>

                {/* 4. Payments & Pricing */}
                <article id="payments-pricing">
                  <h3>4. Payments & Pricing</h3>
                  <ul>
                    <li>
                      All prices are listed in <strong>INR (₹)</strong> unless otherwise
                      stated and may exclude taxes/fees.
                    </li>
                    <li>
                      <strong>Advance/deposit payments</strong> are typically required to
                      confirm bookings and may be non-refundable.
                    </li>
                    <li>
                      Final payment schedules will be mentioned in the
                      invoice/proposal.
                    </li>
                    <li>
                      Late payments may result in service delays, rescheduling,
                      or cancellation.
                    </li>
                    <li>
                      We reserve the right to <strong>correct pricing errors</strong> and
                      update packages or inclusions.
                    </li>
                  </ul>
                </article>

                {/* 5. Refunds & Cancellations */}
                <article id="refunds">
                  <h3>5. Refunds & Cancellations</h3>
                  <ul>
                    <li>
                      <strong>Deposits</strong> are generally non-refundable as they are
                      used to block vendors/venues.
                    </li>
                    <li>
                      Client-initiated cancellations after partial or full
                      payment will follow the stage-based refund slabs mentioned
                      in your proposal or contract.
                    </li>
                    <li>
                      Rescheduling depends on availability and may incur change
                      fees and price differences.
                    </li>
                    <li>
                      If cancellations are caused by{" "}
                      <strong>Parivar Wedding Event’s fault</strong>, we may provide
                      refunds on a reasonable pro-rata basis for undelivered
                      services.
                    </li>
                    <li>
                      Events impacted by <strong>force majeure</strong> are handled in
                      good faith with best-effort solutions or credits.
                    </li>
                  </ul>
                </article>

                {/* 6. Privacy & Data Protection */}
                <article id="privacy">
                  <h3>6. Privacy & Data Protection</h3>
                  <ul>
                    <li>
                      We collect personal data (name, contact details, event
                      preferences) solely to provide and improve services.
                    </li>
                    <li>
                      Your data may be shared with <strong>trusted vendors</strong>{" "}
                      strictly for service delivery and legal compliance.
                    </li>
                    <li>
                      We implement reasonable technical and organizational
                      measures for <strong>data security</strong>, though absolute
                      security cannot be guaranteed.
                    </li>
                    <li>
                      You may request <strong>data access, correction, or deletion</strong>{" "}
                      where legally applicable.
                    </li>
                  </ul>
                </article>

                {/* 7. Cookies & Tracking */}
                <article id="cookies">
                  <h3>7. Cookies & Tracking</h3>
                  <ul>
                    <li>
                      We use cookies for <strong>analytics, personalization, and
                      session management</strong>.
                    </li>
                    <li>
                      You can manage cookies via your browser settings; some
                      features may be unavailable without them.
                    </li>
                  </ul>
                </article>

                {/* 8. User Conduct */}
                <article id="conduct">
                  <h3>8. User Conduct</h3>
                  <p>By using the site, you agree not to:</p>
                  <ul>
                    <li>
                      Post or share unlawful, harmful, defamatory, or abusive
                      content.
                    </li>
                    <li>
                      Attempt to hack, disrupt, or overload our systems or
                      compromise security.
                    </li>
                    <li>
                      Impersonate another person or misrepresent your
                      affiliation.
                    </li>
                    <li>
                      Engage in spam, automated scraping, or unauthorized
                      commercial activity.
                    </li>
                  </ul>
                </article>

                {/* 9. Intellectual Property */}
                <article id="ip-rights">
                  <h3>9. Intellectual Property</h3>
                  <ul>
                    <li>
                      All site content (logos, text, images, code, layouts) is
                      protected by <strong>copyright and trademark</strong> laws.
                    </li>
                    <li>
                      You are granted a <strong>limited, non-transferable, revocable
                      license</strong> to use the site for personal, non-commercial
                      purposes.
                    </li>
                    <li>
                      You may not copy, reproduce, distribute, or create
                      derivative works without prior written consent.
                    </li>
                  </ul>
                </article>

                {/* 10. Liability & Warranties */}
                <article id="liability">
                  <h3>10. Liability & Warranties</h3>
                  <ul>
                    <li>
                      Services are provided on an <strong>“as-is” and “as-available”</strong>{" "}
                      basis without warranties of any kind.
                    </li>
                    <li>
                      We do not warrant uninterrupted, secure, or error-free
                      operation.
                    </li>
                    <li>
                      To the maximum extent permitted by law, our total
                      liability is limited to the <strong>fees you paid</strong> for the
                      affected service.
                    </li>
                    <li>
                      We are not responsible for <strong>indirect or consequential
                      damages</strong> (e.g., lost profits, opportunity costs,
                      emotional distress).
                    </li>
                  </ul>
                </article>

                {/* 11. Payments & Security */}
                <article id="payments-security">
                  <h3>11. Payments & Security</h3>
                  <ul>
                    <li>
                      Payments must be made via authorized channels (e.g.,{" "}
                      <strong>Razorpay, UPI, bank transfer</strong>).
                    </li>
                    <li>
                      Parivar Wedding Event does not store full card details; we
                      rely on secure, PCI-compliant payment gateways.
                    </li>
                    <li>
                      You are responsible for maintaining the confidentiality of
                      your account credentials and payment info.
                    </li>
                  </ul>
                </article>

                {/* 12. Dispute Resolution & Governing Law */}
                <article id="disputes">
                  <h3>12. Dispute Resolution & Governing Law</h3>
                  <ul>
                    <li>
                      These Terms are governed by the <strong>laws of India</strong>.
                    </li>
                    <li>
                      Courts in <strong>Ghaziabad, Uttar Pradesh</strong> shall have exclusive
                      jurisdiction unless otherwise agreed in writing.
                    </li>
                    <li>
                      Parties shall first attempt <strong>amicable resolution</strong>{" "}
                      before legal escalation.
                    </li>
                  </ul>
                </article>

                {/* 13. Changes to Terms */}
                <article id="changes">
                  <h3>13. Changes to Terms</h3>
                  <p>
                    Parivar Wedding Event may update these Terms periodically.
                    Continued use of the website after updates constitutes
                    acceptance. Material changes will be signposted on this page
                    and/or via email.
                  </p>
                </article>

                {/* 14. Contact Us */}
                <article id="contact">
                  <h3>14. Contact Us</h3>
                  <p>
                    For questions or complaints regarding these Terms:
                    <br />
                    📧 Email:{" "}
                    <a href="mailto:support@parivarweddingevent.com">
                      support@parivarweddingevent.com
                    </a>
                    <br />
                    📞 Phone: +91-8920514929 / +91-8439616352
                    <br />
                    📍 Address: C-34 Krishna Garden, Govind Puram, Ghaziabad, Uttar Pradesh, India
                  </p>
                  <p className="muted" style={{ marginTop: 8 }}>
                    ⚖️ Note: This document is for general guidance. For legal
                    compliance (e.g., IT Act 2000, Indian Contract Act, and any
                    international data regulations if applicable), seek
                    professional legal advice.
                  </p>
                </article>

                {/* Acknowledge */}
                <div className="terms-ack">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                    />
                    <span>I have read and agree to the Terms & Policies.</span>
                  </label>

                  <div className="ack-actions">
                    <button
                      className="btn primary"
                      onClick={() => setOpen(false)}
                      disabled={!agreed}
                      ref={lastFocusable}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default TermsIconAdvanced;
